'use client';

// ---------------------------------------------------------------------------
// MusicPlayerHost — renders whichever panel the store says is open
// ---------------------------------------------------------------------------
//
// Lives in MusicProvider, so it survives navigation between route groups. The
// Topbar button is only a trigger that flips `panel` in the store.
//
// Escape steps expanded -> compact -> closed rather than closing outright, so
// someone who expanded the player to find a track is not thrown all the way back
// to the icon by one keypress.
//
// The click-trap overlay follows LanguageSelector.tsx:279 — a `fixed inset-0`
// div that swallows the next click — rather than the `document.addEventListener`
// approach the Topbar menus use. It is the more robust of the two patterns in the
// codebase: the listener version misses a click that lands inside another
// element which stops propagation, and there are several of those in the sidebar.
//
// The overlay sits at z-[59], below the panel (z-[60]) and below the toast
// container (z-[100], ToastProvider.tsx:66) so a toast stays clickable while the
// player is open, but above the topbar (z-40) so clicking a topbar control closes
// the player instead of doing two things at once.
// ---------------------------------------------------------------------------

import { useEffect } from 'react';

import { MusicCompactPanel } from './MusicCompactPanel';
import { MusicExpandedPanel } from './MusicExpandedPanel';
import { useMusicStore } from '../stores/music.store';

export function MusicPlayerHost() {
  const panel = useMusicStore((s) => s.panel);
  const collapsePanel = useMusicStore((s) => s.collapsePanel);
  const setPanel = useMusicStore((s) => s.setPanel);

  // Escape, which the Topbar's own menus do not handle.
  useEffect(() => {
    if (panel === 'closed') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') collapsePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [panel, collapsePanel]);

  if (panel === 'closed') return null;

  return (
    <>
      {/*
        Below the panel (z-[59] vs z-[60]) and above the topbar (z-40), so a click
        anywhere else closes the player without also activating what was clicked.
      */}
      <div
        id="music-panel-overlay"
        className="fixed inset-0 z-[59]"
        aria-hidden="true"
        onClick={() => setPanel('closed')}
        onPointerDown={(e) => {
          e.stopPropagation();
          setPanel('closed');
        }}
      />
      {panel === 'compact' && <MusicCompactPanel />}
      {panel === 'expanded' && <MusicExpandedPanel />}
    </>
  );
}
